<template>
  <section
    class="floating-panel"
    :class="{
      'floating-panel--collapsed': collapsed
    }"
    :style="panelStyle"
  >
    <header 
      class="floating-panel__header" 
      @click="toggleCollapsed"
    >
      <div class="floating-panel__header-left">
        <div
          v-if="$slots.icon"
          class="floating-panel__icon"
        >
          <slot name="icon" />
        </div>

        <div class="floating-panel__titles">
          <div class="floating-panel__title-row">
            <strong class="floating-panel__title">
              {{ title }}
            </strong>

            <span
              v-if="badge !== null && badge !== undefined"
              class="floating-panel__badge"
            >
              {{ badge }}
            </span>
          </div>

          <span
            v-if="subtitle && !collapsed"
            class="floating-panel__subtitle"
          >
            {{ subtitle }}
          </span>
        </div>
      </div>

      <div
        class="floating-panel__header-actions"
        @click.stop
      >
        <slot name="header-actions" />

        <button
          type="button"
          class="floating-panel__header-button"
          :aria-label="collapsed ? 'Expandir' : 'Recolher'"
          @click="toggleCollapsed"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              v-if="collapsed"
              d="M6 15L12 9L18 15"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />

            <path
              v-else
              d="M6 9L12 15L18 9"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <button
          v-if="closable"
          type="button"
          class="floating-panel__header-button floating-panel__header-button--close"
          aria-label="Fechar"
          @click="$emit('close')"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 6L18 18M18 6L6 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </header>

    <transition name="floating-panel-content">
      <div
        v-show="!collapsed"
        class="floating-panel__body"
      >
        <slot />
      </div>
    </transition>

    <div
      v-if="!collapsed && $slots.footer"
      class="floating-panel__footer"
    >
      <slot name="footer" />
    </div>
  </section>
</template>

<script>
export default {
  name: 'FloatingPanel',

  props: {
    title: {
      type: String,
      required: true
    },

    subtitle: {
      type: String,
      default: ''
    },

    badge: {
      type: [String, Number],
      default: null
    },

    collapsed: {
      type: Boolean,
      default: false
    },

    closable: {
      type: Boolean,
      default: false
    },

    /*
      * 0 = caixa mais à direita
      * 1 = próxima caixa à esquerda
      * 2 = próxima...
      */
    positionIndex: {
      type: Number,
      default: 0
    },

    width: {
      type: Number,
      default: 380
    },

    bottom: {
      type: Number,
      default: 0
    },

    right: {
      type: Number,
      default: 24
    },

    gap: {
      type: Number,
      default: 12
    },

    maxBodyHeight: {
      type: Number,
      default: 460
    },

    zIndex: {
      type: Number,
      default: 1000
    }
  },

  emits: [
    'update:collapsed',
    'toggle',
    'close'
  ],

  computed: {
    panelStyle() {
      const calculatedRight =
        this.right +
        (
          this.positionIndex *
          (this.width + this.gap)
        );

      return {
        '--floating-panel-width': `${this.width}px`,
        '--floating-panel-right': `${calculatedRight}px`,
        '--floating-panel-bottom': `${this.bottom}px`,
        '--floating-panel-max-body-height': `${this.maxBodyHeight}px`,
        '--floating-panel-z-index': this.zIndex
      };
    }
  },

  methods: {
    toggleCollapsed() {
      const value = !this.collapsed;

      this.$emit(
        'update:collapsed',
        value
      );

      this.$emit(
        'toggle',
        value
      );
    }
  }
};
</script>

<style scoped>
.floating-panel {
  position: fixed;

  right: var(--floating-panel-right);
  bottom: var(--floating-panel-bottom);

  z-index: var(--floating-panel-z-index);

  display: flex;
  flex-direction: column;

  width: var(--floating-panel-width);
  max-width: calc(100vw - 24px);

  overflow: hidden;

  background: #ffffff;

  border: 1px solid #dfe5eb;
  border-bottom: 0;

  border-radius: 14px 14px 0 0;

  box-shadow:
      0 -2px 8px rgba(15, 23, 42, 0.04),
      0 10px 35px rgba(15, 23, 42, 0.16);

  font-family:
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      Roboto,
      Helvetica,
      Arial,
      sans-serif;

  transition:
      width 0.2s ease,
      box-shadow 0.2s ease;
}

.floating-panel--collapsed {
  box-shadow:
      0 -2px 8px rgba(15, 23, 42, 0.03),
      0 6px 20px rgba(15, 23, 42, 0.12);
}

.floating-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  min-height: 58px;

  padding: 10px 12px 10px 15px;

  background: #ffffff;

  border-bottom: 1px solid #edf1f4;

  cursor: pointer;

  user-select: none;
}

.floating-panel--collapsed .floating-panel__header {
  border-bottom: 0;
}

.floating-panel__header-left {
  display: flex;
  align-items: center;

  min-width: 0;

  gap: 10px;
}

.floating-panel__icon {
  display: flex;
  align-items: center;
  justify-content: center;

  flex: 0 0 auto;

  width: 36px;
  height: 36px;

  color: #0058b7;
  background: #eef4ff;

  border-radius: 10px;
}

.floating-panel__icon :deep(svg) {
  width: 20px;
  height: 20px;
}

.floating-panel__titles {
  min-width: 0;
}

.floating-panel__title-row {
  display: flex;
  align-items: center;

  gap: 7px;

  min-width: 0;
}

.floating-panel__title {
  overflow: hidden;

  color: #172033;

  font-size: 14px;
  font-weight: 700;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.floating-panel__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  flex: 0 0 auto;

  min-width: 21px;
  height: 21px;

  padding: 0 6px;

  color: #ffffff;
  background: #0058b7;

  border-radius: 999px;

  font-size: 11px;
  font-weight: 700;
}

.floating-panel__subtitle {
  display: block;

  margin-top: 2px;

  overflow: hidden;

  color: #778395;

  font-size: 11px;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.floating-panel__header-actions {
  display: flex;
  align-items: center;

  flex: 0 0 auto;

  gap: 2px;

  margin-left: 10px;
}

.floating-panel__header-button {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;

  padding: 0;

  color: #657286;
  background: transparent;

  border: 0;
  border-radius: 8px;

  cursor: pointer;

  transition:
      color 0.15s ease,
      background-color 0.15s ease;
}

.floating-panel__header-button:hover {
  color: #172033;
  background: #f3f6f9;
}

.floating-panel__header-button--close:hover {
  color: #d33232;
  background: #fff1f1;
}

.floating-panel__header-button svg {
  width: 18px;
  height: 18px;
}

.floating-panel__body {
  overflow-x: hidden;
  overflow-y: auto;

  max-height: var(--floating-panel-max-body-height);

  background: #f8fafc;
}

.floating-panel__footer {
  padding: 10px 14px;

  background: #ffffff;

  border-top: 1px solid #edf1f4;
}

/*
 * Scroll discreto
 */
.floating-panel__body::-webkit-scrollbar {
  width: 6px;
}

.floating-panel__body::-webkit-scrollbar-track {
  background: transparent;
}

.floating-panel__body::-webkit-scrollbar-thumb {
  background: #cbd3dc;

  border-radius: 10px;
}

/*
 * Animação
 */
.floating-panel-content-enter-active,
.floating-panel-content-leave-active {
  transition:
      opacity 0.15s ease,
      transform 0.15s ease;
}

.floating-panel-content-enter-from,
.floating-panel-content-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/*
 * Mobile
 *
 * No celular posicionamos a caixa ocupando praticamente
 * toda a largura.
 */
@media (max-width: 700px) {
  .floating-panel {
    right: 12px;

    width: calc(100vw - 24px);
  }
}
</style>